import { zodResolver } from '@hookform/resolvers/zod'
import {
  CaretLeftIcon,
  CaretRightIcon,
  ClockAfternoonIcon,
  ClockIcon,
  HandCoinsIcon,
  MinusIcon,
  PlusIcon,
} from '@phosphor-icons/react'
import { useMemo } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import z from 'zod'

import { useCreateVisitorParkingSession } from '@/api/visitor-parking/create-visitor-parking-session'
import { useGetVisitorParkingCalculation } from '@/api/visitor-parking/get-visitor-parking-calculation'
import { Button } from '@/components/business/visitor-parking/button'
import { ButtonIcon } from '@/components/business/visitor-parking/button-icon'
import { Card } from '@/components/business/visitor-parking/card'
import { DetailsCard } from '@/components/business/visitor-parking/details-card'
import { ErrorMessage } from '@/components/business/visitor-parking/error-message'
import { HelpSection } from '@/components/business/visitor-parking/help-section'
import { SectionHeader } from '@/components/business/visitor-parking/section-header'
import { StepIndicator } from '@/components/business/visitor-parking/step-indicator'
import { Icon } from '@/components/ui/icon'
import { useSearchParams } from '@/hooks'
import { useVisitorParkingStore } from '@/pages/visitor-parking/visitor-parking.store'
import {
  formatCurrency,
  formatDuration,
  formatRelativeParkingEndTime,
  getRelativePaymentSuccessReturnUrl,
} from '@/pages/visitor-parking/visitor-parking.utils'
import { VisitorParkingFormLayout } from '@/pages/visitor-parking/visitor-parking-form-layout'
import { getApiErrorMessage } from '@/utils/error'

import { VISITOR_PARKING_TOKEN_URL_KEY } from '../visitor-parking.constants'
import * as S from './duration-form.styled'

interface DurationFormProps {
  onBack: () => void
  onNext: () => void
}

export const DurationForm = ({ onBack, onNext }: DurationFormProps) => {
  const { t } = useTranslation(['visitor-parking'])
  const { duration, setDuration, setPaymentSession, visitor } = useVisitorParkingStore()
  const { mutateAsync: createVisitorParkingSession, error: createVisitorParkingSessionError } =
    useCreateVisitorParkingSession()
  const navigate = useNavigate()
  const { parsedQuery } = useSearchParams({
    initialState: { [VISITOR_PARKING_TOKEN_URL_KEY]: undefined },
    schema: z.object({ [VISITOR_PARKING_TOKEN_URL_KEY]: z.string().optional() }),
  })
  const token = parsedQuery[VISITOR_PARKING_TOKEN_URL_KEY] ?? null

  const schema = useMemo(
    () =>
      z.object({
        duration: z.number().min(1, t('visitor-parking:durationForm.validation.required')),
      }),
    [t],
  )

  const form = useForm<{ duration: number }>({
    defaultValues: {
      duration: duration,
    },
    mode: 'onChange',
    resolver: zodResolver(schema),
  })

  const durationValue = useWatch({
    control: form.control,
    name: 'duration',
  })

  const {
    data: calculationData,
    isLoading,
    error: calculationError,
  } = useGetVisitorParkingCalculation({
    duration: durationValue,
  })

  const onSubmit = async (values: { duration: number }) => {
    if (!visitor) return

    setDuration(values.duration)

    const session = await createVisitorParkingSession({
      duration: values.duration,
      ...visitor,
    })

    if (session.amount === 0) {
      navigate(getRelativePaymentSuccessReturnUrl(session.ticketNumber, token ?? '', true))
      return
    }

    setPaymentSession({
      amount: session.amount,
      clientSecret: session.clientSecret,
      sessionId: session.sessionId,
      ticketNumber: session.ticketNumber,
      visitorId: session.visitorId,
    })
    onNext()
  }

  return (
    <VisitorParkingFormLayout
      footer={
        <>
          <ButtonIcon type="button" onClick={onBack}>
            <Icon icon={CaretLeftIcon} />
          </ButtonIcon>
          <Button
            disabled={!form.formState.isValid || isLoading || !!calculationError}
            loading={form.formState.isSubmitting}
            type="submit"
            onClick={form.handleSubmit(onSubmit)}
          >
            {t('visitor-parking:durationForm.next')} <Icon icon={CaretRightIcon} />
          </Button>
        </>
      }
    >
      <StepIndicator currentStep={2} totalSteps={3} />
      <SectionHeader
        description={t('visitor-parking:durationForm.sectionDescription')}
        icon={ClockAfternoonIcon}
        title={t('visitor-parking:durationForm.sectionTitle')}
      />
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card>
            <S.DurationLabel htmlFor="duration">
              {t('visitor-parking:durationForm.durationLabel')}
            </S.DurationLabel>
            <Controller
              control={form.control}
              name="duration"
              render={({ field: { onChange, value } }) => (
                <S.DurationSelector>
                  <S.DurationPreview>{formatDuration(value, t)}</S.DurationPreview>
                  <S.DurationSelectorButton
                    disabled={value <= 1}
                    type="button"
                    onClick={() => onChange(value - 1)}
                  >
                    <Icon icon={MinusIcon} />
                  </S.DurationSelectorButton>
                  <S.DurationSelectorButton type="button" onClick={() => onChange(value + 1)}>
                    <Icon icon={PlusIcon} />
                  </S.DurationSelectorButton>
                </S.DurationSelector>
              )}
            />
            <DetailsCard
              icon={ClockIcon}
              loading={isLoading}
              title={t('visitor-parking:durationForm.endTime')}
              value={formatRelativeParkingEndTime(calculationData?.endDate, t)}
            />
            <DetailsCard
              icon={HandCoinsIcon}
              loading={isLoading}
              title={t('visitor-parking:durationForm.fee')}
              value={formatCurrency(calculationData?.amount)}
            />
            <S.CostDescription>
              {t('visitor-parking:durationForm.costDescription')}
            </S.CostDescription>

            {createVisitorParkingSessionError || calculationError ? (
              <ErrorMessage
                message={getApiErrorMessage(calculationError || createVisitorParkingSessionError)}
              />
            ) : null}
          </Card>
        </form>
        <HelpSection />
      </FormProvider>
    </VisitorParkingFormLayout>
  )
}
